import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getFirestore, doc, onSnapshot, getDoc, getDocs, collection, query, where } from 'firebase/firestore';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './StorePage.css';

const db = getFirestore();

const FULFILLMENT_STATUSES = [
  { value: 'pending', label: 'Order Received', description: 'Your order has been placed and is awaiting confirmation.' },
  { value: 'sent_to_kitchen', label: 'Sent to Kitchen', description: 'The store has received your order and sent it to the kitchen.' },
  { value: 'in_preparation', label: 'In Preparation', description: 'Your food is being prepared right now.' },
  { value: 'ready', label: 'Ready for Pickup', description: 'Your order is ready! Head to the store to pick it up.' },
  { value: 'served', label: 'Served', description: 'Your order has been served.' },
  { value: 'out_for_delivery', label: 'Out for Delivery', description: 'Your order is on its way to you.' },
  { value: 'complete', label: 'Complete', description: 'Your order is complete. Enjoy!' },
];

const STATUS_ORDER = {
  pending: 0,
  sent_to_kitchen: 1,
  in_preparation: 2,
  ready: 3,
  served: 4,
  out_for_delivery: 5,
  complete: 6,
};

const OrderTrackingPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user, userCollection, loading: authLoading } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [store, setStore] = useState(null);
  const [storeReview, setStoreReview] = useState(null);
  const [productReviews, setProductReviews] = useState({});
  const [publicOrder, setPublicOrder] = useState(null);

  // Use publicOrder fulfillmentStatus as source of truth when available,
  // falling back to purchase doc. This ensures real-time updates from KDS
  // are reflected immediately even if the cloud function hasn't synced
  // to the purchase doc yet.
  const effectiveFulfillmentStatus = publicOrder?.fulfillmentStatus || order?.fulfillmentStatus || order?.status || 'pending';

  const isOrderComplete = order && (
    effectiveFulfillmentStatus === 'complete' ||
    effectiveFulfillmentStatus === 'completed' ||
    order.status === 'complete' ||
    order.status === 'completed' ||
    order.status === 'open'
  );

  useEffect(() => {
    if (authLoading) return; // wait for auth state to hydrate
    if (!user) {
      navigate('/');
      return;
    }
    const unsub = onSnapshot(
      doc(db, userCollection, user.uid, 'purchases', orderId),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setOrder({
            id: snap.id,
            ...data,
            orderDate: data.orderDate?.toDate ? data.orderDate.toDate() : new Date(data.orderDate),
          });
          // Load store info if needed
          if (data.locationId && !store) {
            loadStore(data.locationId);
          }
          // Load reviews for this order's store (scoped to this order)
          if (data.locationId && user) {
            loadReviews(data.locationId, user.uid, orderId);
          }
        } else {
          setOrder(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error watching order:', err);
        setLoading(false);
      }
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, orderId, authLoading]);

  // Real-time listener for public order (source of truth for fulfillmentStatus)
  useEffect(() => {
    if (!order?.publicOrderId || !order?.locationId) return;
    const publicOrderRef = doc(db, 'publicStores', order.locationId, 'orders', order.publicOrderId);
    const unsub = onSnapshot(publicOrderRef, (snap) => {
      if (snap.exists()) {
        setPublicOrder({ id: snap.id, ...snap.data() });
      }
    }, (err) => {
      console.warn('Error watching public order:', err);
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.publicOrderId, order?.locationId]);

  const loadStore = async (storeId) => {
    try {
      const snap = await getDoc(doc(db, 'publicStores', storeId));
      if (snap.exists()) {
        setStore({ id: snap.id, ...snap.data() });
      }
    } catch (e) {
      console.warn('Failed to load store:', e);
    }
  };

  const loadPublicOrder = async (locationId, publicOrderId) => {
    try {
      const snap = await getDoc(doc(db, 'publicStores', locationId, 'orders', publicOrderId));
      if (snap.exists()) {
        setPublicOrder({ id: snap.id, ...snap.data() });
      }
    } catch (e) {
      console.warn('Failed to load public order:', e);
    }
  };

  const loadReviews = async (storeId, uid, purchaseOrderId) => {
    try {
      // Load store review scoped to this order
      const storeReviewSnap = await getDocs(
        query(
          collection(db, 'publicStores', storeId, 'reviews'),
          where('consumerId', '==', uid),
          where('orderId', '==', purchaseOrderId)
        )
      );
      if (!storeReviewSnap.empty) {
        const r = storeReviewSnap.docs[0];
        setStoreReview({ id: r.id, ...r.data() });
      } else {
        setStoreReview(null);
      }

      // Load product reviews scoped to this order
      const productReviewSnap = await getDocs(
        query(
          collection(db, 'publicStores', storeId, 'productReviews'),
          where('consumerId', '==', uid),
          where('orderId', '==', purchaseOrderId)
        )
      );
      const map = {};
      productReviewSnap.docs.forEach((d) => {
        const data = d.data();
        if (data.productId) map[data.productId] = { id: d.id, ...data };
      });
      setProductReviews(map);
    } catch (e) {
      console.warn('Failed to load reviews:', e);
    }
  };

  const formatCurrency = (val) => {
    const num = typeof val === 'number' ? val : parseFloat(val) || 0;
    return `$${num.toFixed(2)}`;
  };

  const currentStatusIndex = order ? (STATUS_ORDER[effectiveFulfillmentStatus || order.status || 'pending'] ?? 0) : -1;

  if (loading) {
    return (
      <div className="ff-store-page" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <div style={{ flex: 1 }} className="ff-loading-stores">
          <div className="ff-spinner"></div>
          <p>Loading order details...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="ff-store-page" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <div style={{ flex: 1 }} className="text-center py-5">
          <i className="fas fa-exclamation-circle fa-4x text-muted mb-4"></i>
          <h4 className="text-muted">Order not found</h4>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/orders')}>
            <i className="fas fa-arrow-left mr-2"></i>Back to Orders
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="ff-store-page" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <div style={{ flex: 1, padding: '24px 0' }}>
        <div className="container">
          <button className="btn btn-sm btn-outline-secondary mb-3" onClick={() => navigate('/orders')}>
            <i className="fas fa-arrow-left mr-2"></i>My Orders
          </button>

          <div className="row">
            <div className="col-lg-8">
              {/* Order Header */}
              <div className="card mb-4" style={{ borderRadius: 16, border: '1px solid #eee' }}>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h4 className="font-weight-bold mb-1">
                        {store?.locationName || order.storeName || 'Order'}
                      </h4>
                      <div className="text-muted small">
                        #{order.confirmationNumber || order.id.slice(-6).toUpperCase()}{order.guestNumber ? ` · Guest #${order.guestNumber}` : ''} · {order.orderDate?.toLocaleDateString?.()} {order.orderDate?.toLocaleTimeString?.()}
                      </div>
                    </div>
                    <div
                      style={{
                        background: '#fff3cd',
                        color: '#856404',
                        padding: '6px 14px',
                        borderRadius: 20,
                        fontSize: '0.85rem',
                        fontWeight: 600,
                      }}
                    >
                      {effectiveFulfillmentStatus === 'canceled' || order.status === 'canceled'
                        ? 'Canceled'
                        : effectiveFulfillmentStatus
                          ? effectiveFulfillmentStatus.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
                          : 'Pending'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tracking Timeline */}
              <div className="card mb-4" style={{ borderRadius: 16, border: '1px solid #eee' }}>
                <div className="card-body">
                  <h5 className="font-weight-bold mb-4">Order Status</h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {FULFILLMENT_STATUSES.map((step, idx) => {
                      const isDone = idx <= currentStatusIndex && effectiveFulfillmentStatus !== 'canceled' && order.status !== 'canceled';
                      const isCurrent = idx === currentStatusIndex && effectiveFulfillmentStatus !== 'canceled' && order.status !== 'canceled';
                      const isCanceled = (effectiveFulfillmentStatus === 'canceled' || order.status === 'canceled') && idx === 0;
                      return (
                        <div key={step.value} style={{ display: 'flex', gap: 16 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                background: isCanceled ? '#dc3545' : isDone ? '#28a745' : '#e0e0e0',
                                color: isDone || isCanceled ? '#fff' : '#999',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                border: isCurrent ? '3px solid #ff6b35' : 'none',
                              }}
                            >
                              {isCanceled ? <i className="fas fa-times"></i> : isDone ? <i className="fas fa-check"></i> : idx + 1}
                            </div>
                            {idx < FULFILLMENT_STATUSES.length - 1 && (
                              <div
                                style={{
                                  width: 3,
                                  flex: 1,
                                  background: isDone ? '#28a745' : '#e0e0e0',
                                  minHeight: 24,
                                }}
                              />
                            )}
                          </div>
                          <div style={{ paddingBottom: 20, flex: 1 }}>
                            <div
                              style={{
                                fontWeight: isCurrent || isCanceled ? 700 : isDone ? 600 : 400,
                                color: isCanceled ? '#dc3545' : isDone ? '#333' : '#999',
                              }}
                            >
                              {isCanceled ? 'Order Canceled' : step.label}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: isDone || isCanceled ? '#666' : '#aaa', marginTop: 2 }}>
                              {isCanceled ? 'This order has been canceled by the store.' : step.description}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Uber Direct delivery block (3PL). Visible from the
                      moment the order is placed — before dispatch there is
                      no tracking URL yet (the courier is assigned when the
                      kitchen marks the order 'ready'). */}
                  {(publicOrder?.deliveryProvider || order?.deliveryProvider) === 'uber_direct' && (() => {
                    const trackingUrl = publicOrder?.deliveryTrackingUrl || order?.deliveryTrackingUrl;
                    const courierName = publicOrder?.deliveryCourierName || order?.deliveryCourierName;
                    const courierVehicle = publicOrder?.deliveryCourierVehicle || order?.deliveryCourierVehicle;
                    const deliveryStatus = publicOrder?.deliveryStatus || order?.deliveryStatus;
                    const deliveryAddr = publicOrder?.deliveryAddress || order?.deliveryAddress;
                    const deliveryFeeAmt = publicOrder?.deliveryFee ?? order?.deliveryFee;
                    return (
                      <div
                        className="mt-3 p-3"
                        style={{
                          background: '#fff3ee',
                          border: '1px solid rgba(255,87,34,0.35)',
                          borderRadius: 10,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', color: '#ff5722', fontWeight: 700, fontSize: '0.95rem' }}>
                          <i className="fas fa-motorcycle mr-2"></i>Delivery by Uber Direct
                        </div>
                        {!trackingUrl ? (
                          <div style={{ fontSize: '0.85rem', color: '#666', marginTop: 6 }}>
                            A courier will be assigned when your order is ready.
                          </div>
                        ) : (
                          <>
                            {courierName && (
                              <div style={{ fontSize: '0.85rem', color: '#333', fontWeight: 600, marginTop: 6 }}>
                                Courier: {courierName}{courierVehicle ? ` · ${courierVehicle}` : ''}
                              </div>
                            )}
                            {deliveryStatus && (
                              <div style={{ fontSize: '0.78rem', color: '#777', marginTop: 2 }}>
                                Courier status: {String(deliveryStatus).replace(/_/g, ' ')}
                              </div>
                            )}
                          </>
                        )}
                        {deliveryAddr && (
                          <div style={{ fontSize: '0.78rem', color: '#777', marginTop: 4 }}>
                            <i className="fas fa-map-marker-alt mr-1"></i>{deliveryAddr}
                          </div>
                        )}
                        {deliveryFeeAmt > 0 && (
                          <div style={{ fontSize: '0.78rem', color: '#777', marginTop: 2 }}>
                            Delivery fee: ${Number(deliveryFeeAmt).toFixed(2)}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Uber Direct courier tracking link (3PL delivery) */}
                  {(publicOrder?.deliveryTrackingUrl || order?.deliveryTrackingUrl) && (
                    <a
                      href={publicOrder?.deliveryTrackingUrl || order?.deliveryTrackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-block mt-3"
                      style={{
                        border: '1px solid #ff6b35',
                        color: '#ff6b35',
                        borderRadius: 10,
                        fontWeight: 600,
                      }}
                    >
                      <i className="fas fa-truck mr-2"></i>Track your courier (live map)
                    </a>
                  )}
                </div>
              </div>

              {/* Order Items */}
              <div className="card mb-4" style={{ borderRadius: 16, border: '1px solid #eee' }}>
                <div className="card-body">
                  <h5 className="font-weight-bold mb-3">Items</h5>
                  {(order.items || []).map((item, i) => (
                    <div key={i} className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <div className="font-weight-bold">{item.productName}</div>
                        <div className="text-muted small">
                          {formatCurrency(item.unitPrice || item.price)} × {item.qty}
                        </div>
                        {item.options && item.options.length > 0 && (
                          <div className="text-muted small">
                            {item.options.map((o, j) => (
                              <span key={j} className="badge badge-light mr-1" style={{ fontSize: '0.7rem' }}>
                                {o.name || o.optionName}
                              </span>
                            ))}
                          </div>
                        )}
                        {item.instructions && (
                          <div className="text-muted small" style={{ fontStyle: 'italic' }}>
                            “{item.instructions}”
                          </div>
                        )}
                      </div>
                      <div className="font-weight-bold">{formatCurrency((item.unitPrice || item.price || 0) * item.qty)}</div>
                    </div>
                  ))}
                  <hr />
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Subtotal</span>
                    <span>{formatCurrency(order.subtotal)}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Tax</span>
                    <span>{formatCurrency(order.tax)}</span>
                  </div>
                  <div className="d-flex justify-content-between mt-2 pt-2" style={{ borderTop: '1px solid #eee' }}>
                    <span className="font-weight-bold" style={{ fontSize: '1.1rem' }}>Total</span>
                    <span className="font-weight-bold" style={{ fontSize: '1.1rem', color: '#ff6b35' }}>
                      {formatCurrency(order.total)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              {/* Order Info */}
              <div className="card mb-4" style={{ borderRadius: 16, border: '1px solid #eee' }}>
                <div className="card-body">
                  <h5 className="font-weight-bold mb-3">Order Info</h5>
                  <div className="mb-2">
                    <div className="text-muted small">Order Type</div>
                    <div className="font-weight-bold">{order.orderType === 'delivery' ? 'Delivery' : 'Pick Up'}</div>
                  </div>
                  <div className="mb-2">
                    <div className="text-muted small">Payment</div>
                    <div className="font-weight-bold">
                      {order.paymentMethod === 'cash' ? (
                        'Cash'
                      ) : (
                        <>
                          {publicOrder?.cardBrand ? (
                            <>
                              <i className={`fab fa-cc-${publicOrder.cardBrand.toLowerCase()} mr-1`}></i>
                              {publicOrder.cardBrand} ending in {publicOrder.cardLast4 || '****'}
                            </>
                          ) : (
                            order.paymentMethod
                          )}
                          {publicOrder?.paymentStatus === 'paid' && (
                            <span className="badge badge-success ml-2" style={{ fontSize: '0.75rem' }}>Paid</span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {order.deliveryAddress && (
                    <div className="mb-2">
                      <div className="text-muted small">Delivery Address</div>
                      <div className="font-weight-bold">{order.deliveryAddress}</div>
                    </div>
                  )}
                  {order.notes && (
                    <div className="mb-2">
                      <div className="text-muted small">Notes</div>
                      <div className="font-weight-bold" style={{ fontStyle: 'italic' }}>“{order.notes}”</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Rate Your Experience — only show if there are unreviewed items or store */}
              {isOrderComplete && (!storeReview || (order.items || []).some((item) => !productReviews[item.productId])) && (
                <div className="card mb-4" style={{ borderRadius: 16, border: '1px solid #eee' }}>
                  <div className="card-body">
                    <h5 className="font-weight-bold mb-3">
                      <i className="fas fa-star mr-2" style={{ color: '#ffc107' }}></i>Rate Your Experience
                    </h5>

                    {/* Store Review */}
                    {!storeReview && (
                      <div
                        className="d-flex align-items-center justify-content-between mb-3"
                        style={{ padding: '10px 12px', background: '#fafafa', borderRadius: 10, cursor: 'pointer' }}
                        onClick={() => navigate(`/review/${orderId}?type=store`)}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: '50%',
                              background: '#fff3e0',
                              color: '#ff5722',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: 12,
                              fontSize: '0.9rem',
                            }}
                          >
                            <i className="fas fa-store"></i>
                          </div>
                          <div>
                            <div className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
                              Rate the Store
                            </div>
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                              Share your experience with this restaurant
                            </div>
                          </div>
                        </div>
                        <i className="fas fa-chevron-right text-muted" style={{ fontSize: '0.8rem' }}></i>
                      </div>
                    )}

                    {/* Product Reviews — only show unreviewed items */}
                    {(order.items || [])
                      .filter((item) => !productReviews[item.productId])
                      .map((item, idx) => (
                        <div
                          key={idx}
                          className="d-flex align-items-center justify-content-between mb-2"
                          style={{ padding: '8px 12px', background: '#fafafa', borderRadius: 10, cursor: 'pointer' }}
                          onClick={() => navigate(`/review/${orderId}?type=product&productId=${item.productId}`)}
                        >
                          <div className="d-flex align-items-center">
                            <div
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                background: '#fff3e0',
                                color: '#ff5722',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: 10,
                                fontSize: '0.8rem',
                              }}
                            >
                              <i className="fas fa-utensils"></i>
                            </div>
                            <div>
                              <div className="font-weight-bold" style={{ fontSize: '0.85rem' }}>
                                Rate {item.productName}
                              </div>
                              <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                How was this item?
                              </div>
                            </div>
                          </div>
                          <i className="fas fa-chevron-right text-muted" style={{ fontSize: '0.75rem' }}></i>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Need Help */}
              <div className="card" style={{ borderRadius: 16, border: '1px solid #eee' }}>
                <div className="card-body">
                  <h5 className="font-weight-bold mb-2">Need Help?</h5>
                  <p className="text-muted small">
                    If you have any issues with your order, please contact the store directly.
                  </p>
                  {store?.phone && (
                    <a href={`tel:${store.phone}`} className="btn btn-outline-primary btn-sm btn-block">
                      <i className="fas fa-phone mr-2"></i>Call {store.phone}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OrderTrackingPage;
